/**
 * usePrefetchDatesEffect — Effect 5 of the /map state machine (AK-193).
 *
 * Spec: ADR-037 «3× map perf» Tier-2 #6 / audit F1
 *   (docs/superpowers/specs/2026-06-25-satellite-pipeline-perf-audit.md)
 *
 * Problem (audit F1):
 *   Stepping the date in SatelliteStrip swaps `ndvi-active-raster`'s URL to a
 *   date whose tiles are NOT in MapLibre's cache → the overlay blanks, the
 *   backend cold-renders each tile (3-5s), then they pop in. Every neighbour
 *   click pays the full cold path.
 *
 * Fix (frontend, zero backend dependency):
 *   When `selectedDate` settles for an active raster product, proactively warm
 *   the immediate prev/next date's tiles FOR THE CURRENT VIEWPORT via
 *   low-priority `Image()` loads. Tiles are immutable per (date,scope,z,x,y)
 *   and nginx caches them (`200 30d`), so the prefetch primes nginx + the
 *   browser HTTP cache. When the user then steps to that date, MapLibre's
 *   fetch is a warm cache hit → no blank→pop.
 *
 * Why Image() and not addSource:
 *   We must NOT touch the live MapLibre style (adding a hidden source would
 *   render or churn layers — the riskovaja-zone regression class). A detached
 *   `Image()` shares the browser's HTTP cache with MapLibre's tile fetches
 *   (same origin, same URL, same cache key), so the warm-up is invisible to
 *   the map yet lands in the exact cache MapLibre will read.
 *
 * Scope / cost guards:
 *   - Only the ACTIVE raster product is prefetched (NDVI/CONTRAST/GNR). RGB +
 *     cloud-mask are not stepped by the date strip in the common flow, so we
 *     skip them to keep prefetch volume bounded.
 *   - Viewport tiles are capped (viewportTiles `cap`) so a wide low-zoom view
 *     can't fire hundreds of requests.
 *   - A module-scoped Set dedups URLs already prefetched this session — a
 *     pan/re-render with the same date+viewport never re-warms.
 *   - Prefetch is debounced 250ms after the date settles so rapid stepping
 *     doesn't warm intermediate dates the user blew past.
 *   - `fetchPriority='low'` (where supported) keeps prefetch off the critical
 *     path of the tiles the user is actively viewing.
 */
import { useEffect } from 'react';
import type maplibregl from 'maplibre-gl';
import { useMapStore } from '../useMapStoreV2';
import { productOf } from '../derived/productOf';
import { availableKey } from '../derived/availableKey';
import { adjacentDates } from '../derived/adjacentDates';
import { viewportTiles } from '../derived/viewportTiles';
import { buildRasterUrl } from './rasterSourceEffect';
import { rasterTileSize } from '../../featureFlags';
import { useActiveSeason } from '../../season/SeasonContext';

const RASTER_MINZOOM = 9;
const RASTER_MAXZOOM = 16;
const DEBOUNCE_MS = 250;

// Session-wide dedup of warmed URLs. Bounded growth is acceptable — a session
// steps through at most a few dozen dates × ~24 tiles; the Set is cheap and
// reset on full reload.
const warmed = new Set<string>();

/** Fire a detached, low-priority image prefetch for a concrete tile URL. */
function warmUrl(url: string): void {
  if (warmed.has(url)) return;
  warmed.add(url);
  const img = new Image();
  // fetchPriority is a recent addition; the cast keeps TS happy on older libs.
  (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'low';
  img.decoding = 'async';
  // No onload/onerror handlers needed — we only want the byte transfer into
  // the HTTP cache. Errors (404 on a truly empty tile) are harmless.
  img.src = url;
}

export function usePrefetchDatesEffect(map: maplibregl.Map | null): void {
  const activeRasterMode = useMapStore((s) => s.activeRasterMode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  const activeScopeType = useMapStore((s) => s.activeScope.type);
  const activeScopeId = useMapStore((s) => s.activeScope.id);
  const availableDates = useMapStore((s) => s.availableDates);

  const { season } = useActiveSeason();
  const seasonId = season?.seasonId ?? null;

  useEffect(() => {
    if (!map) return;
    const product = productOf(activeRasterMode);
    // Only warm a real raster overlay with a settled date.
    if (!product || !selectedDate) return;

    const key = availableKey(activeScopeType, activeScopeId, product, seasonId);
    const set = availableDates.get(key);
    if (!set || set.size === 0) return;

    const { prev, next } = adjacentDates(Array.from(set), selectedDate);
    if (!prev && !next) return;

    const timer = window.setTimeout(() => {
      const tileSize = rasterTileSize();
      const b = map.getBounds();
      const tiles = viewportTiles(
        { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
        map.getZoom(),
        tileSize,
        RASTER_MINZOOM,
        RASTER_MAXZOOM,
      );
      if (tiles.length === 0) return;

      for (const date of [next, prev]) {
        if (!date) continue;
        const template = buildRasterUrl(activeRasterMode, date, activeScopeType, activeScopeId);
        if (!template) continue;
        for (const { z, x, y } of tiles) {
          const url = template
            .replace('{z}', String(z))
            .replace('{x}', String(x))
            .replace('{y}', String(y));
          warmUrl(url);
        }
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    map,
    activeRasterMode,
    selectedDate,
    activeScopeType,
    activeScopeId,
    availableDates,
    seasonId,
  ]);
}
